import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Package, Plus, Clock, Cog, CheckCircle2 } from "lucide-react";import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { api, gbp } from "@/lib/api";

const STATUS_META = {
  Received: { icon: Clock, cls: "bg-amber-100 text-amber-800", desc: "We've received your order." },
  "In Progress": { icon: Cog, cls: "bg-blue-100 text-blue-800", desc: "Currently being done." },
  Ready: { icon: CheckCircle2, cls: "bg-green-100 text-green-800", desc: "Ready for collection!" },
};

export default function MyOrders() {
  const { customer } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (customer) {
      api
        .get("/customer/orders")
        .then((res) => setOrders(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else if (customer === false) {
      setLoading(false);
    }
  }, [customer]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

  if (customer === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  if (customer === false) {
    return (
      <div className="App" data-testid="my-orders-page">
        <SiteHeader />
        <div className="mx-auto flex max-w-md flex-col items-center px-6 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
            <Package size={26} />
          </span>
          <h1 className="mt-6 font-display text-3xl font-extrabold">Sign in to view your orders</h1>
          <p className="mt-2 text-[var(--text-secondary)]">
            Track the status of every order you place with NLCS.
          </p>
          <Link
            to="/login"
            data-testid="orders-signin-cta"
            className="mt-6 inline-flex items-center gap-2 bg-[var(--brand-primary)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-primary-hover)]"
          >
            Sign in
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="App" data-testid="my-orders-page">
      <SiteHeader />

      <div className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-end justify-between gap-4 px-6 py-12 lg:px-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-primary)]">
              My Orders
            </p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
              Hi {customer?.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="mt-2 text-lg text-[var(--text-secondary)]">
              Track the status of every order you've placed.
            </p>
          </div>
          <Link
            to="/order"
            data-testid="new-order-button"
            className="inline-flex items-center gap-2 bg-[var(--brand-primary)] px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[var(--brand-primary-hover)]"
          >
            <Plus size={16} /> New order
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10 lg:px-10">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[var(--brand-primary)]" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center border border-dashed border-black/15 bg-white py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
              <Package size={26} />
            </span>
            <h2 className="mt-5 font-display text-2xl font-bold">No orders yet</h2>
            <p className="mt-2 text-[var(--text-secondary)]">Place your first order to see it here.</p>
            <Link
              to="/order"
              className="mt-6 inline-flex items-center gap-2 bg-[var(--brand-primary)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-primary-hover)]"
            >
              <Plus size={16} /> Place an order
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o, i) => {
              const meta = STATUS_META[o.status] || STATUS_META.Received;
              const StatusIcon = meta.icon;
              return (
                <motion.div
                  key={o.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="border border-black/10 bg-white p-6"
                  data-testid={`my-order-${o.order_number}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-mono text-xs font-semibold text-[var(--text-secondary)]">
                        {o.order_number}
                      </div>
                      <div className="mt-1 font-display text-lg font-bold">{gbp(o.total)}</div>
                      <div className="text-sm text-[var(--text-secondary)]">{fmtDate(o.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold ${meta.cls}`}
                        data-testid={`order-status-${o.order_number}`}
                      >
                        <StatusIcon size={15} /> {o.status || "Received"}
                      </span>
                      <p className="mt-1.5 text-xs text-[var(--text-secondary)]">{meta.desc}</p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2 border-t border-black/10 pt-4">
                    {o.services.map((s, idx) => (
                      <span
                        key={idx}
                        className="border border-black/10 bg-[#F9F9F8] px-3 py-1 text-xs"
                      >
                        {s.label} × {s.quantity}
                      </span>
                    ))}
                  </div>

                  {/* Progress bar */}
                  <div className="mt-5 flex items-center gap-2">
                    {["Received", "In Progress", "Ready"].map((step, idx) => {
                      const order = ["Received", "In Progress", "Ready"];
                      const current = order.indexOf(o.status || "Received");
                      const active = idx <= current;
                      return (
                        <React.Fragment key={step}>
                          <div className="flex flex-col items-center">
                            <span
                              className={`h-2.5 w-2.5 rounded-full ${
                                active ? "bg-[var(--brand-primary)]" : "bg-black/15"
                              }`}
                            />
                            <span className={`mt-1 text-[10px] ${active ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}>
                              {step}
                            </span>
                          </div>
                          {idx < 2 && (
                            <span className={`h-px flex-1 ${idx < current ? "bg-[var(--brand-primary)]" : "bg-black/15"}`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
